import { PropertyType } from '@/api/enumerations';
import { AggHomogenizationData } from '@/api/workOrders/types';
import { independentStructuresGroup } from '@/constants/selectOptions';
import { fmtCurrency, fmtPercent } from '@/helpers';
import { Box, Divider, Grid, Typography } from '@mui/material';
import { memo } from 'react';

import { GroupStatusIcon } from '../../components/StatusIndicators';
import { RESULT_FIELD_LABELS } from '../constants';
import {
  isEvaluationResultComplete,
  isResultValueFormationComplete,
} from '../utils/completionCheckers';

interface EvaluationResultProps {
  aggHomogenizationData: AggHomogenizationData;
  propertyType: PropertyType;
}

export const EvaluationResult = memo(function EvaluationResult(
  props: EvaluationResultProps
) {
  const { aggHomogenizationData, propertyType } = props;

  const areaLabel = independentStructuresGroup.has(propertyType)
    ? 'Área construída'
    : 'Área privativa';

  return (
    <Grid id="fac-result" container spacing={3} marginBottom={3}>
      <Grid item xs={12}>
        <Box display="flex" alignItems="center" gap={1}>
          <GroupStatusIcon
            isComplete={isEvaluationResultComplete(aggHomogenizationData)}
          />
          <Typography my={2} fontWeight={600}>
            Resultado da avaliação
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={3}>
        <Typography variant="caption" color="text.secondary">
          {RESULT_FIELD_LABELS.sanitized_mean}
        </Typography>
        <Typography>
          {fmtCurrency(aggHomogenizationData.sanitized_mean)}
        </Typography>
      </Grid>
      <Grid item xs={3}>
        <Typography variant="caption" color="text.secondary">
          {RESULT_FIELD_LABELS.lower_limit}
        </Typography>
        <Typography>
          {fmtCurrency(aggHomogenizationData.lower_limit)}
        </Typography>
      </Grid>
      <Grid item xs={3}>
        <Typography variant="caption" color="text.secondary">
          {RESULT_FIELD_LABELS.upper_limit}
        </Typography>
        <Typography>
          {fmtCurrency(aggHomogenizationData.upper_limit)}
        </Typography>
      </Grid>
      <Grid item xs={3}>
        <Typography variant="caption" color="text.secondary">
          {RESULT_FIELD_LABELS.market_value}
        </Typography>
        <Typography>
          {fmtCurrency(aggHomogenizationData.market_value)}
        </Typography>
      </Grid>
      <Grid item xs={3}>
        <Typography variant="caption" color="text.secondary">
          {RESULT_FIELD_LABELS.lower_limit_total}
        </Typography>
        <Typography>
          {fmtCurrency(aggHomogenizationData.lower_limit_total)}
        </Typography>
      </Grid>
      <Grid item xs={3}>
        <Typography variant="caption" color="text.secondary">
          {RESULT_FIELD_LABELS.upper_limit_total}
        </Typography>
        <Typography>
          {fmtCurrency(aggHomogenizationData.upper_limit_total)}
        </Typography>
      </Grid>
      <Grid item xs={3}>
        <Typography variant="caption" color="text.secondary">
          {RESULT_FIELD_LABELS.lower_limit_confidence}
        </Typography>
        <Typography>
          {fmtPercent(aggHomogenizationData.lower_limit_confidence)}
        </Typography>
      </Grid>
      <Grid item xs={3}>
        <Typography variant="caption" color="text.secondary">
          {RESULT_FIELD_LABELS.upper_limit_confidence}
        </Typography>
        <Typography>
          {fmtPercent(aggHomogenizationData.upper_limit_confidence)}
        </Typography>
      </Grid>
      <Grid id="fac-formation" item xs={12}>
        <Divider />
        <Box display="flex" alignItems="center" gap={1}>
          <GroupStatusIcon
            isComplete={isResultValueFormationComplete(aggHomogenizationData)}
          />
          <Typography my={2} fontWeight={600}>
            Formação do Valor
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={3}>
        <Typography variant="caption" color="text.secondary">
          {areaLabel}
        </Typography>
        <Typography>{aggHomogenizationData.area ?? '-'}</Typography>
      </Grid>
      <Grid item xs={3}>
        <Typography variant="caption" color="text.secondary">
          {RESULT_FIELD_LABELS.market_value_rounded_1pct_plus}
        </Typography>
        <Typography>
          {fmtCurrency(aggHomogenizationData.market_value_rounded_1pct_plus)}
        </Typography>
      </Grid>
      <Grid item xs={3}>
        <Typography variant="caption" color="text.secondary">
          {RESULT_FIELD_LABELS.market_value_rounded_1pct_minus}
        </Typography>
        <Typography>
          {fmtCurrency(aggHomogenizationData.market_value_rounded_1pct_minus)}
        </Typography>
      </Grid>
      <Grid item xs={3}>
        <Typography variant="caption" color="text.secondary">
          {RESULT_FIELD_LABELS.rounded_market_value}
        </Typography>
        <Typography>
          {fmtCurrency(aggHomogenizationData.rounded_market_value)}
        </Typography>
      </Grid>
    </Grid>
  );
});
